import { Box } from '@mui/material';
import type { Plugin } from 'chart.js';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { num } from '../format';
import { COLORS } from '../theme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/**
 * flank#4758 — `interaction: { intersect: false }` only matches bars inside Chart.js's
 * `chartArea`; it never even runs its hit-testing once the pointer is over a category
 * label, which lives in the axis margin outside that area (Chart.js's own
 * `_getActiveElements` short-circuits to the previous selection there). That broke the
 * "hover a wrapped 2-line label to see its bar's tooltip" half of #4744. This plugin
 * covers just that margin: it maps the pointer's position on the category axis back to
 * the nearest tick and drives the tooltip/hover state manually. Hovering inside the plot
 * area is untouched — it still goes through the default interaction above.
 */
export const categoryLabelHoverPlugin: Plugin<'bar'> = {
  id: 'categoryLabelHover',
  afterEvent(chart, { event }) {
    if (event.type !== 'mousemove') return;
    const horizontal = chart.options.indexAxis === 'y';
    const categoryScale = chart.scales[horizontal ? 'y' : 'x'];
    if (!categoryScale) return;

    const { x, y } = event;
    if (x == null || y == null) return;
    const { chartArea } = chart;
    const inMargin = horizontal
      ? x < chartArea.left && y >= chartArea.top && y <= chartArea.bottom
      : y > chartArea.bottom && x >= chartArea.left && x <= chartArea.right;
    if (!inMargin) return;

    const labelCount = chart.data.labels?.length ?? 0;
    const index = Math.max(0, Math.min(labelCount - 1, categoryScale.getValueForPixel(horizontal ? y : x) ?? 0));
    const element = chart.getDatasetMeta(0).data[index];
    if (!element) return;

    const active = [{ datasetIndex: 0, index }];
    if (chart.tooltip?.getActiveElements()[0]?.index === index) return;
    chart.setActiveElements(active);
    chart.tooltip?.setActiveElements(active, { x, y });
    chart.render();
  },
};

/** Longest single-line label we render before ellipsising, per orientation. */
const MAX_LABEL = { horizontal: 24, vertical: 14 };

const truncate = (label: string, limit: number) => (label.length > limit ? `${label.slice(0, limit - 1)}…` : label);

/**
 * flank#4744 — horizontal bars (Top Campaigns) have the left margin to spare, so
 * word-wrapping a long category onto up to 2 lines reads better than single-line
 * ellipsis-truncating it ("Whipple Plumbing | SLC.Mark…"). Vertical charts keep the
 * single-line `truncate` above — their category ticks are already rotated 45° to
 * use the space, and stacking lines under a rotated tick doesn't read cleanly.
 */
export const wrapHorizontalLabel = (label: string, charsPerLine = MAX_LABEL.horizontal): string[] => {
  if (label.length <= charsPerLine) return [label];

  const words = label.split(' ');
  let line = '';
  let i = 0;
  for (; i < words.length; i++) {
    const candidate = line ? `${line} ${words[i]}` : words[i];
    if (candidate.length > charsPerLine && line) break;
    line = candidate;
    if (candidate.length >= charsPerLine) {
      i++;
      break;
    }
  }
  if (i >= words.length) return [line];

  const rest = words.slice(i).join(' ');
  return [line, rest.length > charsPerLine ? truncate(rest, charsPerLine) : rest];
};

interface Props {
  labels: string[];
  values: number[];
  height?: number;
  /** Format for the tooltip — keep this full precision. */
  format?: (value: unknown) => string;
  /** Format for value-axis ticks; defaults to `format`. Prefer a compact form. */
  tickFormat?: (value: unknown) => string;
  /** Horizontal bars (Top Campaigns) vs vertical columns (day-of-week). */
  horizontal?: boolean;
  /**
   * Second tick line under each category — used for daypart hour ranges, which
   * have to be readable without hovering anything (FB-003).
   */
  tickSubLabels?: string[];
}

/** Column / bar chart matching the loveable reference. */
const BarChart = ({
  labels,
  values,
  height = 260,
  format = num,
  tickFormat,
  horizontal = false,
  tickSubLabels,
}: Props) => {
  // Category ticks receive the category *index* as `value` in Chart.js — never run
  // the numeric formatter on that axis (that produced "0,1,2…" instead of names).
  //
  // autoSkip is off deliberately: with it on, Chart.js silently drops every
  // other category whenever the full names don't fit, so half the bars were
  // unlabelled. Truncate (and rotate, when vertical) instead — the tooltip
  // below still shows the untruncated name.
  const categoryTicks = {
    color: COLORS.muted,
    autoSkip: false,
    maxRotation: horizontal ? 0 : 45,
    minRotation: 0,
    // Chart.js renders an array as stacked tick lines — used both for the daypart
    // hour range under its name, and (horizontal only) to wrap a long campaign
    // name onto a 2nd line instead of single-line truncating it (flank#4744).
    callback: (_value: string | number, index: number) => {
      const label = labels[index] ?? '';
      if (horizontal) return wrapHorizontalLabel(label);
      const truncated = truncate(label, MAX_LABEL.vertical);
      const sub = tickSubLabels?.[index];
      return sub ? [truncated, sub] : truncated;
    },
  };

  const valueTicks = {
    color: COLORS.muted,
    maxTicksLimit: 5,
    autoSkip: true,
    callback: (value: string | number) => (tickFormat ?? format)(value),
  };

  return (
    <Box sx={{ height, width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Bar
        plugins={[categoryLabelHoverPlugin]}
        data={{
          labels,
          datasets: [
            {
              label: '',
              data: values,
              backgroundColor: COLORS.primary,
              borderRadius: 4,
              maxBarThickness: horizontal ? 18 : 26,
            },
          ],
        }}
        options={{
          indexAxis: horizontal ? 'y' : 'x',
          responsive: true,
          maintainAspectRatio: false,
          // Default intersect:true only fires the tooltip with the cursor directly over
          // the bar's rectangle — hovering its category tick (drawn outside the bar in
          // the axis margin, e.g. the campaign name to the left of a horizontal bar)
          // never matched. Matching on the shared category axis instead lets either the
          // bar or its label trigger the tooltip (flank#4744 AC1).
          interaction: { mode: horizontal ? 'y' : 'x', intersect: false },
          plugins: {
            // chartjs-plugin-datalabels is registered globally by legacy charts; opt out here.
            datalabels: false as never,
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: items => {
                  const index = items[0]?.dataIndex ?? 0;
                  return labels[index] ?? '';
                },
                label: context => format(horizontal ? context.parsed.x : context.parsed.y),
              },
            },
          },
          scales: horizontal
            ? {
                x: {
                  beginAtZero: true,
                  border: { display: false },
                  grid: { color: COLORS.border },
                  ticks: valueTicks,
                },
                y: {
                  grid: { display: false },
                  border: { display: false },
                  ticks: categoryTicks,
                },
              }
            : {
                x: {
                  grid: { display: false },
                  ticks: categoryTicks,
                },
                y: {
                  beginAtZero: true,
                  border: { display: false },
                  grid: { color: COLORS.border },
                  ticks: valueTicks,
                },
              },
        }}
      />
    </Box>
  );
};

export default BarChart;
